import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

// Types
export interface PropertyInput {
  property_address: string;
  living_area: string;
  bedrooms: string;
  bathrooms: string;
  year_built: string;
  lot_size?: string;
  garage_spaces?: string;
  pool?: boolean;
  city?: string;
  postal_code?: string;
  customerName?: string;
  lead_id?: string;          // NEW: Link to lead for CMA assignment
  
  // Critical XGBoost Features - Added for model alignment
  list_price?: string;          // Current/asking price - CRITICAL for model
  has_hoa?: boolean;            // HOA presence
  hoa_fee?: string;             // Monthly HOA fee
  stories?: string;             // Number of stories
  has_guest_house?: boolean;    // Guest house presence
  guest_house_sq_ft?: string;   // Guest house square footage
  has_basement?: boolean;       // Basement presence
  basement_sq_ft?: string;      // Basement square footage
  basement_finished?: boolean;  // Finished basement
  fireplace_count?: string;      // Number of fireplaces
}

export interface MLSData {
  // Basic property info
  listing_id?: string;
  mls_number?: string;
  apn?: string;
  raw_apn?: string;
  address?: string;
  bedrooms?: number;
  bathrooms?: number;
  living_area?: number;  // Primary field from Python script (matches ARMLS LivingArea)
  sqft?: number;         // Alias for living_area for backward compatibility
  year_built?: number;
  lot_size?: number;
  garage_spaces?: number;
  pool?: boolean;
  city?: string;
  state?: string;
  postal_code?: string;
  property_type?: string;
  property_subtype?: string;
  
  // Enhanced MLS fields (newly added from backend integration)
  stories?: number;              // Number of stories/floors
  
  // Pricing information
  list_price?: number;
  close_price?: number;
  estimated_price?: number;
  price_per_sqft?: number;
  assessed_value?: number;
  
  // Listing details
  list_date?: string;
  close_date?: string;
  days_on_market?: number;
  status?: string;
  mls_status?: string;  // Primary status field from Python response
  standard_status?: string;
  
  // Location data
  latitude?: number;
  longitude?: number;
  geocoding?: {
    lat: number;
    lng: number;
  };
  subdivision?: string;
  school_district?: string;
  
  // Agent/Brokerage info - Using Python script field names
  agent_name?: string;
  brokerage?: string;
  listing_agent_full_name?: string;
  listing_office_name?: string;
  listing_agent_phone?: string;
  listing_office_phone?: string;
  buyer_office_name?: string;
  buyer_agent_full_name?: string;
  co_listing_office_name?: string;
  co_listing_agent_full_name?: string;
  
  // Additional MLS fields
  pool_yn?: boolean;
  association_yn?: boolean;
  association_fee?: number;
  photos_count?: number;
  public_remarks?: string;
  modification_timestamp?: string;
  
  // County/Records data
  mcr?: string;
  ownership?: string;
  section_township_range?: string;
  last_sale_price?: string | number;    // Made flexible for string or number
  last_sale_date?: string;
  deed_date?: string;
  
  // Enhanced MLS listings data (from integration)
  mls_listings?: any[];          // Array of MLS listing objects
  search_methods_used?: string[]; // Methods used to find this data
  confidence_score?: number;     // Confidence score from integration
  
  // Enhanced data sources metadata
  data_sources?: {
    county_source?: string;
    mls_source?: string;
    prioritization_applied?: boolean;
  };
  
  // Comparable search results
  total_comparables_found?: number;
  comparable_search_success?: boolean;
  comparable_properties?: {
    total_found: number;
    enhanced_comparables?: any[];
    geographic_comparables?: any[];
  };
  
  // System metadata
  photos?: Array<{
    id?: string;
    name?: string;
    caption?: string;
    description?: string;
    primary?: boolean;
    thumb?: string;
    small?: string;
    medium?: string;
    large?: string;
    xl?: string;
    xxl?: string;
    original?: string;
    url?: string;
  }>;
  confidence?: number;
  source?: string;
  note?: string;
  features?: string[];
}

export interface PropertyData extends PropertyInput {
  validated: boolean;
  aiDescription?: string;
}

export interface PredictionResult {
  success: boolean;
  prediction?: {
    value: number;
    confidence_score: number;
    value_range?: { low: number; high: number };
    feature_importance?: Array<{
      name: string;
      impact: number;
      importance: number;
    }>;
  };
  market_context?: {
    comparables: any[];
    market_stats: any;
    position_analysis: any;
  };
}

export interface WorkflowState {
  // Navigation
  currentStep: 1 | 2 | 3 | 4;
  canProceed: boolean;
  canGoBack: boolean;
  
  // Data progression
  propertyInput?: PropertyInput;
  mlsData?: MLSData;
  validatedData?: PropertyData;
  predictionResult?: PredictionResult;
  
  // UI state
  loading: boolean;
  error?: string;
  progress: number; // 0-100
  currentOperation?: string; // e.g., "Searching MLS database..."
  
  // Missing properties that are being set in Step1PropertyInput
  isPropertyDataLoaded?: boolean;
  propertyDataSource?: string;
  confidenceScore?: number;
}

// Actions
type WorkflowAction =
  | { type: 'SET_STEP'; payload: 1 | 2 | 3 | 4 }
  | { type: 'SET_PROPERTY_INPUT'; payload: PropertyInput }
  | { type: 'SET_MLS_DATA'; payload: MLSData }
  | { type: 'SET_VALIDATED_DATA'; payload: PropertyData }
  | { type: 'SET_PREDICTION_RESULT'; payload: PredictionResult }
  | { type: 'SET_LOADING'; payload: { loading: boolean; operation?: string } }
  | { type: 'SET_ERROR'; payload: string | undefined }
  | { type: 'SET_PROGRESS'; payload: number }
  | { type: 'RESET_WORKFLOW' };

// Initial state
const initialState: WorkflowState = {
  currentStep: 1,
  canProceed: false,
  canGoBack: false,
  loading: false,
  progress: 0,
};

// Reducer
function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'SET_STEP':
      console.log('🔥 [REDUCER DEBUG] SET_STEP reducer called, old step:', state.currentStep, 'new step:', action.payload);
      // Custom progress mapping for each step
      const stepProgressMap = {
        1: 0,    // Step 1: 0% (starting)
        2: 25,   // Step 2: 25% (property input complete)
        3: 75,   // Step 3: 75% (AI analysis complete)
        4: 100   // Step 4: 100% (dashboard complete)
      };
      
      const newState = {
        ...state,
        currentStep: action.payload,
        canGoBack: action.payload > 1,
        progress: stepProgressMap[action.payload],
      };
      console.log('🔥 [REDUCER DEBUG] SET_STEP new state created, currentStep:', newState.currentStep);
      return newState;
    
    case 'SET_PROPERTY_INPUT':
      return {
        ...state,
        propertyInput: action.payload,
        canProceed: !!action.payload.property_address,
        progress: 25,
      };
    
    case 'SET_MLS_DATA':
      return {
        ...state,
        mlsData: action.payload,
      };
    
    case 'SET_VALIDATED_DATA':
      return {
        ...state,
        validatedData: action.payload,
        progress: 50,
      };
    
    case 'SET_PREDICTION_RESULT':
      return {
        ...state,
        predictionResult: action.payload,
        progress: 75,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload.loading,
        currentOperation: action.payload.operation,
        error: action.payload.loading ? undefined : state.error,
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    
    case 'SET_PROGRESS':
      return {
        ...state,
        progress: action.payload,
      };
    
    case 'RESET_WORKFLOW':
      return initialState;
    
    default:
      return state;
  }
}

// Context
export interface WorkflowContextType {
  state: WorkflowState;
  dispatch: React.Dispatch<WorkflowAction>;
  
  // Helper functions
  setStep: (step: 1 | 2 | 3 | 4) => void;
  setPropertyInput: (data: PropertyInput) => void;
  setMLSData: (data: MLSData) => void;
  setValidatedData: (data: PropertyData) => void;
  setPredictionResult: (result: PredictionResult) => void;
  setLoading: (loading: boolean, operation?: string) => void;
  setError: (error: string | undefined) => void;
  resetWorkflow: () => void;
  
  // Navigation helpers
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  
  // Update methods
  updatePropertyData: (data: Partial<PropertyInput>) => void;
  updateValidatedData: (data: Partial<PropertyData>) => void;
  updateMLSData: (data: MLSData) => void;
  updateWorkflowState: (updates: Partial<WorkflowState>) => void;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

// Provider component
interface WorkflowProviderProps {
  children: ReactNode;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(workflowReducer, initialState);
  
  // Helper functions
  const setStep = useCallback((step: 1 | 2 | 3 | 4) => {
    console.log('🔥 [PROVIDER DEBUG] setStep called with step:', step);
    dispatch({ type: 'SET_STEP', payload: step });
    console.log('🔥 [PROVIDER DEBUG] dispatch called for SET_STEP');
  }, [dispatch]);
  
  const setPropertyInput = (data: PropertyInput) => {
    dispatch({ type: 'SET_PROPERTY_INPUT', payload: data });
  };
  
  const setMLSData = (data: MLSData) => {
    dispatch({ type: 'SET_MLS_DATA', payload: data });
  };
  
  const setValidatedData = (data: PropertyData) => {
    dispatch({ type: 'SET_VALIDATED_DATA', payload: data });
  };
  
  const setPredictionResult = (result: PredictionResult) => {
    dispatch({ type: 'SET_PREDICTION_RESULT', payload: result });
  };
  
  const setLoading = (loading: boolean, operation?: string) => {
    dispatch({ type: 'SET_LOADING', payload: { loading, operation } });
  };
  
  const setError = (error: string | undefined) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };
  
  const resetWorkflow = () => {
    dispatch({ type: 'RESET_WORKFLOW' });
  };
  
  const goToNextStep = useCallback(() => {
    console.log('🔥 [PROVIDER DEBUG] goToNextStep called, current step:', state.currentStep);
    if (state.currentStep < 4) {
      const nextStep = (state.currentStep + 1) as 1 | 2 | 3 | 4;
      console.log('🔥 [PROVIDER DEBUG] Advancing to step:', nextStep);
      dispatch({ type: 'SET_STEP', payload: nextStep });
      console.log('🔥 [PROVIDER DEBUG] dispatch called directly for step:', nextStep);
    } else {
      console.log('🔥 [PROVIDER DEBUG] Cannot advance beyond step 4');
    }
  }, [state.currentStep, dispatch]);
  
  const goToPreviousStep = useCallback(() => {
    if (state.currentStep > 1) {
      const prevStep = (state.currentStep - 1) as 1 | 2 | 3 | 4;
      console.log('🔥 [PROVIDER DEBUG] Going back to step:', prevStep);
      dispatch({ type: 'SET_STEP', payload: prevStep });
    }
  }, [state.currentStep, dispatch]);
  
  const updatePropertyData = (data: Partial<PropertyInput>) => {
    const currentData = state.propertyInput || {} as PropertyInput;
    dispatch({ type: 'SET_PROPERTY_INPUT', payload: { ...currentData, ...data } as PropertyInput });
  };
  
  const updateValidatedData = (data: Partial<PropertyData>) => {
    const currentData = state.validatedData || { validated: false } as PropertyData;
    dispatch({ type: 'SET_VALIDATED_DATA', payload: { ...currentData, ...data } as PropertyData });
  };
  
  const updateMLSData = (data: MLSData) => {
    dispatch({ type: 'SET_MLS_DATA', payload: data });
  };
  
  const updateWorkflowState = (updates: Partial<WorkflowState>) => {
    if (updates.loading !== undefined) {
      dispatch({ type: 'SET_LOADING', payload: { loading: updates.loading, operation: updates.currentOperation } });
    }
    if (updates.error !== undefined) {
      dispatch({ type: 'SET_ERROR', payload: updates.error });
    }
    if (updates.progress !== undefined) {
      dispatch({ type: 'SET_PROGRESS', payload: updates.progress });
    }
    if (updates.currentStep !== undefined) {
      dispatch({ type: 'SET_STEP', payload: updates.currentStep });
    }
    if (updates.propertyInput) {
      dispatch({ type: 'SET_PROPERTY_INPUT', payload: updates.propertyInput });
    }
    if (updates.mlsData) {
      dispatch({ type: 'SET_MLS_DATA', payload: updates.mlsData });
    }
    if (updates.validatedData) {
      dispatch({ type: 'SET_VALIDATED_DATA', payload: updates.validatedData });
    }
    if (updates.predictionResult) {
      dispatch({ type: 'SET_PREDICTION_RESULT', payload: updates.predictionResult });
    }
  };
  
  const value: WorkflowContextType = {
    state,
    dispatch,
    setStep,
    setPropertyInput,
    setMLSData,
    setValidatedData,
    setPredictionResult,
    setLoading,
    setError,
    resetWorkflow,
    goToNextStep,
    goToPreviousStep,
    updatePropertyData,
    updateValidatedData,
    updateMLSData,
    updateWorkflowState,
  };
  
  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

// Hook to use the workflow context
export const useWorkflow = (): WorkflowContextType => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

export default WorkflowProvider; 